import { CartAddEvent, CartErrorEvent } from '@theme/events';

class CartDrawerUpsell extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.addEventListener('submit', this.#handleSubmit, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    event.preventDefault();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const button = form.querySelector('button[type="submit"]');
    const error = this.#getErrorElement(form);
    const variantId = new FormData(form).get('id');

    if (!(button instanceof HTMLButtonElement) || !variantId) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    error?.classList.add('hidden');

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = new Set();
    if (this.dataset.sectionId) {
      sectionIds.add(this.dataset.sectionId);
    }

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.add(item.dataset.sectionId);
      }
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
            },
          ],
          sections: Array.from(sectionIds).join(','),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();

      if (data.status) {
        this.dispatchEvent(new CartErrorEvent('cart-drawer-upsell', data.message, data.description, data.errors));
        this.#showError(form, data.message);
        return;
      }

      const itemCount = this.#getCartItemCount(data.sections) ?? 1;

      this.dispatchEvent(
        new CartAddEvent(data, variantId.toString(), {
          source: 'cart-drawer-upsell',
          itemCount,
          variantId: variantId.toString(),
          sections: data.sections,
        })
      );
    } catch (errorMessage) {
      console.error(errorMessage);
      this.#showError(form, 'N\u00e3o foi poss\u00edvel adicionar este produto agora.');
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  /**
   * @param {HTMLFormElement} form
   * @param {string} message
   */
  #showError(form, message) {
    const error = this.#getErrorElement(form);
    if (!(error instanceof HTMLElement)) return;

    error.textContent = message;
    error.classList.remove('hidden');
  }

  /** @param {HTMLFormElement} form */
  #getErrorElement(form) {
    return form.closest('.cart-drawer-upsell__content')?.querySelector('.cart-drawer-upsell__error');
  }

  /** @param {Record<string, string> | undefined} sections */
  #getCartItemCount(sections) {
    if (!sections) return null;

    for (const sectionHtml of Object.values(sections)) {
      const section = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const count = section.querySelector('[ref="cartItemCount"]')?.textContent;

      if (count) {
        const itemCount = Number.parseInt(count.trim(), 10);
        if (!Number.isNaN(itemCount)) return itemCount;
      }
    }

    return null;
  }
}

if (!customElements.get('cart-drawer-upsell')) {
  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}
